import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {CommentActions, type CommentData} from '@github-ui/commenting/CommentActions'
import type {CommentHeaderProps} from '@github-ui/commenting/CommentHeader'
import {useCurrentRepository} from '@github-ui/current-repository'
import {MarkdownEditHistoryViewerQueryComponent} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {encodePart} from '@github-ui/paths'
import {ReactionViewerQueryComponent} from '@github-ui/reaction-viewer/ReactionViewer'
import {ReactionViewerLoading} from '@github-ui/reaction-viewer/ReactionViewerLoading'
import {useCSRFToken} from '@github-ui/use-csrf-token'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Suspense, useRef, useState} from 'react'

import {mapCommitCommentToComment} from '../../../hooks/use-fetch-commit-thread'
import type {CommitComment} from '../../../types/commit-types'
import type {Commit} from '../../../types/shared'
import {shortSha} from '../../../utils/short-sha'
import {UpdateCommitComment} from './UpdateCommitComment'

export function ExistingCommitComments({
  comments,
  commit,
  locked,
  deleteComment,
  setComments,
  setNewCommentContent,
}: {
  comments: CommitComment[]
  commit: Commit
  locked: boolean
  deleteComment: (comment: CommitComment) => void
  setComments: (comments: CommitComment[]) => void
  setNewCommentContent: (content?: string) => void
}) {
  const setComment = (comment: CommitComment) => {
    setComments(comments.map(c => (c.id === comment.id ? comment : c)))
  }

  if (comments.length === 0) {
    return null
  }

  return (
    <div className="d-flex flex-column gap-3">
      {comments.map(comment => {
        return (
          <ExistingCommitComment
            key={comment.id}
            comment={comment}
            commit={commit}
            locked={locked}
            deleteComment={deleteComment}
            setComment={setComment}
            setNewCommentContent={setNewCommentContent}
          />
        )
      })}
    </div>
  )
}

function ExistingCommitComment({
  comment,
  commit,
  locked,
  deleteComment,
  setComment,
  setNewCommentContent,
}: {
  comment: CommitComment
  commit: Commit
  locked: boolean
  deleteComment: (comment: CommitComment) => void
  setComment: (comment: CommitComment) => void
  setNewCommentContent: (content?: string) => void
}) {
  const repo = useCurrentRepository()

  const deleteUrl = `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment`
  const deleteToken = useCSRFToken(deleteUrl, 'delete')
  const [isEditing, setIsEditing] = useState(false)
  const [isMinimized, setIsMinimized] = useState(comment.isHidden)
  const commentRef = useRef<HTMLDivElement>(null)

  const onDelete = async () => {
    if (!confirm('Are you sure you want to delete this?')) {
      return
    }

    const formData = new FormData()

    formData.append('_method', 'delete')
    // eslint-disable-next-line github/authenticity-token
    formData.append('authenticity_token', deleteToken!)
    formData.append('commit_comment[id]', comment.id.toString())
    const resonse = await verifiedFetch(deleteUrl, {
      method: 'POST',
      body: formData,
    })

    if (resonse.ok) {
      deleteComment(comment)
    } else {
      // TODO - handle error
    }
  }

  const onUpdate = (updatedComment: CommitComment) => {
    setComment(updatedComment)
    setIsEditing(false)
  }

  const onHide = async (reason: string) => {
    const formData = new FormData()
    formData.append('_method', 'put')
    formData.append('classifier', reason)

    const response = await verifiedFetch(
      `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment/${comment.id}/minimize`,
      {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      },
    )

    if (response.ok) {
      setComment({...comment, isHidden: true, minimizedReason: reason})
      setIsMinimized(true)
    } else {
      // TODO - handle error
    }
  }

  const onUnhide = async () => {
    const formData = new FormData()
    formData.append('_method', 'put')

    const response = await verifiedFetch(
      `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment/${comment.id}/unminimize`,
      {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      },
    )

    if (response.ok) {
      setComment({...comment, isHidden: false, minimizedReason: null})
      setIsMinimized(false)
    } else {
      // TODO - handle error
    }
  }

  // how to handle multiple authors?
  const commentSubjectAuthorLogin = commit.authors.length > 0 ? commit.authors[0]?.login : ''

  const commentDataWithoutFragment: Omit<CommentData, ' $fragmentSpreads'> = {
    ...mapCommitCommentToComment(comment, commit, repo),
    referenceText: shortSha(commit.oid),
  }

  return (
    <div className="border rounded-2" ref={commentRef} id={comment.urlFragment}>
      <CommitCommentHeader
        comment={commentDataWithoutFragment as CommentData}
        commentAuthorLogin={comment.author.login}
        commentSubjectAuthorLogin={commentSubjectAuthorLogin}
        commentSubjectType="commit"
        avatarUrl={comment.author.avatarUrl}
        isMinimized={isMinimized}
        editComment={() => {
          setIsEditing(true)
        }}
        onReplySelect={setNewCommentContent}
        onMinimize={setIsMinimized}
        navigate={() => {}}
        hideComment={onHide}
        unhideComment={onUnhide}
        deleteComment={onDelete}
        commentRef={commentRef}
        showEditHistory={comment.viewerCanReadUserContentEdits && !!comment.lastUserContentEdit}
        commitComment={comment}
      />
      {isMinimized ? null : isEditing ? (
        <div className="m-2">
          <UpdateCommitComment
            comment={comment}
            commitOid={commit.oid}
            onUpdate={onUpdate}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className="d-flex flex-column m-3 gap-3" style={{gap: '12px'}}>
          <div className="markdown-body" data-turbolinks="false">
            <MarkdownViewer
              disabled={false}
              verifiedHTML={comment.htmlBody}
              markdownValue={comment.body}
              onChange={() => {}}
              onLinkClick={() => {}}
              teamHovercardsEnabled={true}
            />
          </div>
          <Suspense fallback={<ReactionViewerLoading />}>
            <ReactionViewerQueryComponent id={comment.relayId} subjectLocked={locked} />
          </Suspense>
        </div>
      )}
    </div>
  )
}
interface CommitCommentHeaderProps extends CommentHeaderProps {
  showEditHistory: boolean
  commitComment: CommitComment
}

function CommitCommentHeader({hideActions, ...props}: CommitCommentHeaderProps) {
  let editHistoryComponent = null

  if (props.showEditHistory) {
    const comment = props.commitComment

    editHistoryComponent = (
      <Suspense fallback={null}>
        <MarkdownEditHistoryViewerQueryComponent id={comment.relayId} />
      </Suspense>
    )
  }

  return (
    <ActivityHeader
      {...props}
      actions={hideActions ? undefined : <CommentActions {...props} editHistoryComponent={editHistoryComponent} />}
    />
  )
}

try{ ExistingCommitComments.displayName ||= 'ExistingCommitComments' } catch {}
try{ ExistingCommitComment.displayName ||= 'ExistingCommitComment' } catch {}
try{ CommitCommentHeader.displayName ||= 'CommitCommentHeader' } catch {}